import { useId, useRef, useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';
import { mediaApi } from '../../api/media.js';
import { ApiError } from '../../lib/apiClient.js';
import { TilePreview } from '../../components/TilePreview.js';

export interface ImageUploadFieldProps {
  /** Fallback initials and the tile's accessible context — the product or category name. */
  label: string;
  color: string;
  imageUrl: string;
  onImageUrlChange(url: string): void;
  disabled?: boolean;
  /** Tile size in px — the same `TilePreview` this renders elsewhere at, so a 96px form preview and a 40px table thumbnail share one component. */
  size?: number;
  /** Hides the "or paste a URL" fallback — used in the compact table-edit-mode cell, where there is no room for it. Defaults to shown, matching every existing call site. */
  showUrlField?: boolean;
  /** Overrides the tile button's accessible name (default `media.upload`) — table-edit mode disambiguates rows with `products.tableEdit.fieldLabel`. */
  ariaLabel?: string;
  /**
   * Table-cell variant: the tile and nothing else in the flow. The
   * "click to upload" hint moves to the tile's `title`, and an upload failure
   * is shown as an overlay badge on the tile instead of as a line of text
   * underneath it.
   *
   * This is the direct fix for OneDev xyntara/equinox #1: that hint is a full
   * sentence, and inside a ~72px image column it wrapped to one word per line
   * and grew the row far past every other cell in it. A hint is not allowed to
   * reshape what it annotates (ROLES-DESIGNER.md, "Layout stability").
   */
  compact?: boolean;
}

/**
 * The image tile IS the upload affordance: click it (or focus it and press
 * Enter/Space — free, because this is a real `<button>`) and the native file
 * dialog opens via a hidden `<input type="file">`. There is no separate,
 * always-visible file input any more — see
 * docs/decisions/2026-07-31-click-image-upload.md for why, and why the
 * category form got the same treatment for consistency.
 *
 * Upload state and failures are always visible: the tile dims and shows
 * "uploading…" while in flight, and a failed upload leaves the current image
 * exactly as it was, with a human-readable reason underneath, so a bad
 * upload never silently loses whatever image was already set. The URL field
 * is kept as the secondary path it already was.
 */
export function ImageUploadField({
  label,
  color,
  imageUrl,
  onImageUrlChange,
  disabled,
  size = 96,
  showUrlField = true,
  ariaLabel,
  compact = false,
}: ImageUploadFieldProps) {
  const { t } = useI18n();
  const urlInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const result = await mediaApi.upload(file);
      onImageUrlChange(result.url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setUploading(false);
      // Cleared so picking the exact same file again still fires `onChange`
      // (the browser otherwise treats an unchanged file input as a no-op).
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function openPicker() {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  }

  return (
    <div
      className={compact ? 'image-upload image-upload--compact' : 'image-upload'}
      style={compact ? undefined : { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', alignItems: 'flex-start' }}
    >
      <button
        type="button"
        className="tile-upload"
        aria-label={ariaLabel ?? t('media.upload')}
        aria-busy={uploading}
        disabled={disabled || uploading}
        title={compact ? t('media.clickToUpload') : undefined}
        onClick={openPicker}
      >
        <TilePreview label={label || '?'} color={color} imageUrl={imageUrl || null} size={size} />
        {uploading && (
          <span className="tile-upload__overlay" aria-hidden="true">
            {compact ? '…' : t('media.uploading')}
          </span>
        )}
        {compact && error && !uploading && (
          <span className="tile-upload__overlay tile-upload__overlay--error" aria-hidden="true" title={error}>
            !
          </span>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        accept="image/jpeg,image/png,image/webp"
        disabled={disabled || uploading}
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
      {!compact && <span className="field-hint">{t('media.clickToUpload')}</span>}
      {error &&
        (compact ? (
          // Announced, never laid out: the visible signal is the badge on the
          // tile above, whose `title` carries the same text for a mouse user.
          <span className="sr-only" role="alert">
            {error}
          </span>
        ) : (
          <span className="field-error" role="alert">
            {error}
          </span>
        ))}
      {showUrlField && (
        <label htmlFor={urlInputId} style={{ width: '100%' }}>
          {t('media.orUrl')}
          <input
            id={urlInputId}
            type="url"
            value={imageUrl}
            disabled={disabled}
            onChange={(e) => onImageUrlChange(e.target.value)}
            placeholder="https://…"
          />
        </label>
      )}
    </div>
  );
}
